import './pos-app.scss';

import type { LoadFailure } from '@pos/app/scripts/services/load-failure';
import type { NavigateTo } from '@core/app/scripts/services/navigate-to';
import { Barcodes } from '@pos/app/scripts/services/barcodes';
import { DiscountRulesManager } from '@pos/app/scripts/services/discount-rules-manager';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { Domain } from '@shared/app/scripts/services/domain';
import { EndOfShift } from '@pos/app/scripts/services/end-of-shift';
import { Ga } from '@core/app/scripts/services/ga';
import { LegacyEvents } from '@core/app/scripts/services/legacy-events';
import { LocationManager } from '@shared/app/scripts/services/location-manager';
import { LoginHandler } from '@core/app/scripts/services/login-handler';
import { SalesManager } from '@shared/app/scripts/services/sales-manager';
import { Session } from '@shared/app/scripts/services/session';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { TimeKeeper } from '@core/app/scripts/services/time-keeper';

/*
 * Component responsible for /pos-app route.
 */

angular
  .module('posApp')
  .directive(
    'posApp',
    (
      $location: ng.ILocationService,
      $log: ng.ILogService,
      $q: ng.IQService,
      $window: ng.IWindowService,
      barcodes: Barcodes,
      cashierManager,
      discountRulesManager: DiscountRulesManager,
      displayMessage: DisplayMessage,
      domain: Domain,
      endOfShift: EndOfShift,
      ga: Ga,
      isTest,
      legacyEvents: LegacyEvents,
      loadFailure: LoadFailure,
      locationManager: LocationManager,
      loginHandler: LoginHandler,
      navigateTo: NavigateTo,
      pos,
      posInstance,
      posReasons,
      salesManager: SalesManager,
      session: Session,
      settingsManager: SettingsManager,
      translate: ng.translate.ITranslateService,
      timeKeeper: TimeKeeper,
    ) => {
      let template = require('./pos-app.html').default;

      // workaround for getting rid of data-role=page in unit tests
      if (isTest) {
        template = '<div></div>';
      }

      return {
        restrict: 'E',

        scope: true,

        template,

        link: function ($scope: any, element, attrs) {
          const $translate = translate;

          $scope.loaded = false;

          const host = window.location.host;
          const qliqpointBodyClass = 'host-qliqpoint';

          if (host.includes('qliqpoint')) {
            $scope.logoUrl = 'img/qliqpoint-logo.png';
            $scope.faviconUrl = `favicon-qliqpoint.ico?v=${Date.now()}`;
            document.body.classList.add(qliqpointBodyClass);
          } else {
            $scope.logoUrl = 'img/s.png';
            $scope.faviconUrl = `favicon.ico?v=${Date.now()}`;
            document.body.classList.remove(qliqpointBodyClass);
          }

          $scope.$on('$destroy', () => {
            document.body.classList.remove(qliqpointBodyClass);
          });

          // Remove existing favicon links
          const existingFavicons = document.querySelectorAll(
            'link[rel="icon"], link[rel="shortcut icon"]',
          );
          existingFavicons.forEach((link) => link.parentNode.removeChild(link));

          // Create and append the new favicon link
          const newFavicon = document.createElement('link');
          newFavicon.rel = 'icon';
          newFavicon.href = $scope.faviconUrl;
          newFavicon.type = 'image/x-icon';

          const newShortcutIcon = document.createElement('link');
          newShortcutIcon.rel = 'shortcut icon';
          newShortcutIcon.href = $scope.faviconUrl;
          newShortcutIcon.type = 'image/x-icon';

          document.head.appendChild(newFavicon);
          document.head.appendChild(newShortcutIcon);

          const settingsInitPromise = settingsManager.init(),
            sessionPromise = session.init();

          $q.all([sessionPromise, settingsInitPromise]).then(
            ([currentSession, settings]) => {
              if (currentSession.type === 'device') {
                $scope.loaded = true;

                navigateTo('pos.route#main');
              } else {
                if ($window.location.pathname !== '/redirect.html') {
                  $window.location.pathname = '/redirect.html';
                }
              }

              $log.log('[EOS] Setting up EOS');
              endOfShift.init().then(
                () => {},
                () => {
                  $log.info('[EOS] eos/init failed to load!');
                  loadFailure($translate.instant('EOSInitFailure'));
                },
              );

              //Download POS Feed
              barcodes.init();
            },
          );

          // TODO cover with unit tests
          sessionPromise.then((currentSession) => {
            timeKeeper.init();

            if (!currentSession) {
              $window.location.href = domain.link(
                `login#/login/${encodeURIComponent($location.absUrl())}`,
              );

              return;
            }

            ga.set('dimension1', currentSession.username);
          });

          sessionPromise.then(
            (currentSession: any) => {
              if (currentSession?.applications?.pos) {
                const device_id = currentSession.deviceId,
                  wh = currentSession.warehouse,
                  name = currentSession.username;

                posReasons.init().then(
                  () => {
                    $log.log('loaded reasons!');
                  },
                  () => {
                    $log.error(
                      '[Discount Reasons] There was an error getting the Discount Reasons!',
                    );
                  },
                );

                posInstance.init();

                cashierManager.init().then(angular.noop, (error) => {
                  displayMessage.message(
                    $translate.instant('CashiersNotLoaded'),
                  );
                });

                salesManager.init().then(
                  (counters) => {
                    $log.log('Next receipt: ' + counters.receipts);
                    $log.log('Next transit: ' + counters.transits);
                    $log.log('Next consignment: ' + counters.consignments);
                  },
                  () => {
                    displayMessage.message({
                      type: 'errorprompt',
                      ok: true,
                      message: $translate.instant('Error104'),
                      callback: () => loginHandler.redirectToGateway('home'),
                    });
                  },
                );

                locationManager.init().then(
                  () => {
                    pos.transaction = pos.setWarehouse(pos.transaction);

                    const postData =
                      'warehouse__' +
                      locationManager.current.wh +
                      '/format__json';

                    if (pos.config.token) {
                      legacyEvents.add('opencashregister', postData);
                    }

                    // load location success
                    $log.log(
                      `[Warehouses] Loaded ${locationManager.all.length} Warehouses...`,
                    );
                  },
                  () => {
                    // load location error
                    displayMessage.message(
                      $translate.instant('WarehousesNotLoaded'),
                    );
                  },
                );

                //Set Selected Device
                pos.selectedDevice = {
                  device_id: device_id,
                  wh: wh,
                  name: name,
                };

                if (!device_id) {
                  $log.error(
                    '[Device] Error 101 - Missing required device_id field',
                  );
                  loadFailure($translate.instant('Error101'));
                  return;
                }

                if (!wh) {
                  $log.error(
                    '[Device] Error 102 - Missing required wh code field!',
                  );
                  loadFailure($translate.instant('Error102'));
                  return;
                }

                pos.startNewTransaction();

                if (pos.config.selectedCashier) {
                  pos.transaction = pos.setCashier(
                    pos.transaction,
                    pos.config.selectedCashier,
                  );
                }

                pos.config.cashregisterNr = wh;

                discountRulesManager.init();
              }
            },
            (error) => {
              $log.error('[Device] Error 100 - failed to load /me');
              loadFailure($translate.instant('Error100'));
            },
          );
        },
      };
    },
  );
